import React from 'react'

import {
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  ResponsiveValue,
} from '@gousto-internal/citrus-react'
import type { RecipeGridContentBlock, StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { RecipeCount } from '../CollectionScreen/RecipeCount'
import { RecipeTile } from './RecipeTile'
import type { RecipeTileCustomisationData } from './RecipeTile/RecipeTile.types'

type RecipeGridTileState = {
  isInBasket: boolean
  canAddRecipes: boolean
  reachedLimit: boolean
}

type RecipeGridTileAction = (recipeId: RecipeUUID, recipePosition: number) => void

const idleCustomisationData: RecipeTileCustomisationData = {
  isLoadingCustomisations: false,
  hasCustomisationError: false,
  hasOpenedCustomisations: false,
}

type RecipeGridProps = {
  contentBlock: RecipeGridContentBlock
  recipeCountTextColor?: string
  getRecipeTileState: (recipeId: RecipeUUID) => RecipeGridTileState
  getRecipeCustomisationData?: (recipeId: RecipeUUID) => RecipeTileCustomisationData
  onRecipeClick: RecipeGridTileAction
  onAddClick: RecipeGridTileAction
  onRemoveClick: RecipeGridTileAction
  onBlockedAddAttempt: () => void
  onCustomiseOpen?: RecipeGridTileAction
  onCustomiseClose?: () => void
}

const recipeGridItemWidth: ResponsiveValue<string> = [
  '100%',
  '100%',
  'calc((100% - 16px) / 2)',
  'calc((100% - 32px) / 3)',
]

const isStandardRecipeItem = (item: StandardRecipeItem): item is StandardRecipeItem =>
  item.type === 'standard-recipe'

export const RecipeGrid = ({
  contentBlock,
  recipeCountTextColor,
  getRecipeTileState,
  getRecipeCustomisationData,
  onRecipeClick,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  onCustomiseOpen,
  onCustomiseClose,
}: RecipeGridProps) => (
  <Box
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    gap={3}
    data-testid="recipe-grid"
  >
    <Box paddingH={[3, 3, 0]}>
      <RecipeCount count={contentBlock.recipeCount} textColor={recipeCountTextColor} />
    </Box>
    <Box
      display={Display.Flex}
      wrap
      justifyContent={JustifyContent.Center}
      gap={[3, 3, 4]}
      paddingH={[3, 3, 0]}
      data-testid="recipe-grid-items"
    >
      {contentBlock.items.filter(isStandardRecipeItem).map((item, index) => {
        const recipePosition = index + 1
        const recipeTileState = getRecipeTileState(item.id)
        const customisationData = getRecipeCustomisationData?.(item.id) ?? idleCustomisationData

        return (
          <Box
            key={item.id}
            display={Display.Flex}
            flexBasis={recipeGridItemWidth}
            flexGrow={0}
            flexShrink={0}
            maxWidth={recipeGridItemWidth}
          >
            <RecipeTile
              recipe={item}
              isInBasket={recipeTileState.isInBasket}
              canAddRecipes={recipeTileState.canAddRecipes}
              reachedLimit={recipeTileState.reachedLimit}
              onClick={(recipeId) => onRecipeClick(recipeId, recipePosition)}
              onAddClick={(recipeId) => onAddClick(recipeId, recipePosition)}
              onRemoveClick={(recipeId) => onRemoveClick(recipeId, recipePosition)}
              onBlockedAddAttempt={onBlockedAddAttempt}
              customisationData={customisationData}
              onCustomiseOpen={(recipeId) => onCustomiseOpen?.(recipeId, recipePosition)}
              onCustomiseClose={onCustomiseClose}
            />
          </Box>
        )
      })}
    </Box>
  </Box>
)
